/**
 * Domínio: página de Plugins.
 *
 * A própria tela que lista plugins é extensível por plugin — é o que permite,
 * por exemplo, uma loja/catálogo aparecer ali sem que o app conheça a loja.
 *
 * Acesse via `plugins.slots.PAGE_PANEL`.
 *
 * Slots num registro só (`pluginsSlots`); `slots`/`slotContext` e os tipos
 * `PluginsPageSlotContext`/`PluginCardSlotContext` derivam dele.
 */
import type { IPluginProvides } from "@/plugin";
import { defineSlotRegistry, legacySlotContextOf, slotNamesOf, t, type SlotContextMapOf } from "@/ui/slotSchema";
import { temaDesc } from "./app";

/**
 * A página de Plugins como um todo. Não há plugin "do card" aqui. `instalados`
 * permite a um painel (ex: loja) marcar o que já está instalado sem refazer a
 * consulta que a página já fez.
 */
const pluginsPage = {
  total: t.note(t.number, "quantos plugins a página está listando"),
  instalados: t.note(t.array(t.string), "ids dos plugins instalados nesta empresa"),
  ehMaster: t.note(t.boolean, "usuário é super admin da empresa master (dona da plataforma)"),
  // Opcionais porque o contrato tem que aguentar um app mais antigo que ainda
  // não manda o campo — quem consome trata a ausência com um padrão.
  tema: t.optional(t.note(temaDesc, "tema ativo do app")),
  idioma: t.optional(t.note(t.string, 'idioma da interface ("pt", "en", "es"…) — o HTML deste slot é montado no servidor do plugin, que não vê o PluginRuntime')),
} as const;

/** O card de UM plugin. `channel` opcional: plugin sem canal aparece do mesmo jeito. */
const pluginCard = {
  pluginId: t.string,
  channel: t.optional(t.note(t.string, "ausente em plugin sem capability de canal")),
  displayName: t.string,
  version: t.optional(t.string),
  isRemote: t.note(t.boolean, "roda em processo próprio (container/porta)"),
  offline: t.note(t.boolean, "backend sem conexão com o plugin agora"),
  baseUrl: t.optional(t.string),
  provides: t.note(t.typed<IPluginProvides>("IPluginProvides"), "o que o plugin oferece"),
  plugin: t.optional(t.note(t.typed<Record<string, any>>("Record<string, any>"), "o objeto COMPLETO do plugin (fallback)")),
} as const;

export const pluginsSlots = defineSlotRegistry({
  PAGE_HEADER: {
    name: "plugins-page-header",
    description: 'Botões do cabeçalho da página de Plugins, ao lado de "Atualizar" e "Adicionar Plugin Remoto".',
    context: pluginsPage,
  },
  PAGE_PANEL: {
    name: "plugins-page-panel",
    description:
      "Faixa de largura total ENTRE o cabeçalho e a lista — painéis inteiros (loja, aviso de atualização, " +
      "resumo de consumo). Espera mountJsx/htmlPage, não um botão.",
    context: pluginsPage,
  },
  PAGE_TABS: {
    name: "plugins-page-tabs",
    description:
      'ABA própria na página de Plugins, ao lado de "Instalados" e "Padrões": label vira o título, o conteúdo ' +
      "é servido pelo plugin (POST no endpoint, HTML injetado na sessão — sem iframe).",
    context: pluginsPage,
  },
  CARD_ACTIONS: {
    name: "plugins-card-actions",
    description: 'Rodapé do card de UM plugin, junto de "Configurar" — ações por plugin ("atualizar", "ver na loja").',
    context: pluginCard,
  },
  CARD_BADGE: {
    name: "plugins-card-badge",
    description: 'Linha de etiquetas do card, junto das capacidades — marcadores curtos ("atualização disponível").',
    context: pluginCard,
  },
});

export const plugins = {
  events: {},
  /** Slots de render — derivado de `pluginsSlots`. */
  slots: slotNamesOf(pluginsSlots),
  hooks: {},
  /** @deprecated Formato antigo, derivado do registro. Use `pluginsSlots`. */
  slotContext: legacySlotContextOf(pluginsSlots),
  registry: pluginsSlots,
} as const;

export type PluginsSlotContextMap = SlotContextMapOf<typeof pluginsSlots>;
/** plugins-page-header / plugins-page-panel / plugins-page-tabs. */
export type PluginsPageSlotContext = PluginsSlotContextMap["plugins-page-header"];
/** plugins-card-actions / plugins-card-badge. */
export type PluginCardSlotContext = PluginsSlotContextMap["plugins-card-actions"];
